import { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { API_BASE, PINPAD_URL } from "../config";
import { asegurarVersionVigente } from "../utilities/versionApp";
import { apiFetch } from "../utilities/apiFetch";

const useHistorialExternos = (user, externoSeleccionado) => {
  const [datosHistorial, setDatosHistorial] = useState([]);
  const [cargandoHistorial, setCargandoHistorial] = useState(false);
  const [modalPendientes, setModalPendientes] = useState(false);
  const [datosPendientes, setDatosPendientes] = useState([]);
  const [cargandoPendientes, setCargandoPendientes] = useState(false);
  const [modalValidacion, setModalValidacion] = useState(false);
  const [refSeleccionada, setRefSeleccionada] = useState(null);
  const [folioBanco, setFolioBanco] = useState("");
  const [bancoSeleccionado, setBancoSeleccionado] = useState("");
  const [loadingValidacion, setLoadingValidacion] = useState(false);
  const [modoModal, setModoModal] = useState("validar");

  const rolCajero = String(localStorage.getItem("cajero_rol") || "");
  const tienePinpad = rolCajero === "2";

  useEffect(() => {
    if (externoSeleccionado) {
      handleAbrirHistorial();
    } else {
      setDatosHistorial([]);
    }
  }, [externoSeleccionado]);

  const handleAbrirHistorial = async () => {
    if (!externoSeleccionado) return;
    setCargandoHistorial(true);
    try {
      const res = await apiFetch(`${API_BASE}/wMultipagosObtenerHistorialExternos.php?matricula=${externoSeleccionado}`);
      const data = await res.json();
      setDatosHistorial(data.success ? data.data : []);
    } catch (error) {
      setDatosHistorial([]);
    } finally {
      setCargandoHistorial(false);
    }
  };

  const handleAbrirPendientes = async () => {
    if (!externoSeleccionado) return [];
    setModalPendientes(true);
    setCargandoPendientes(true);
    try {
      const res = await apiFetch(`${API_BASE}/wMultipagosObtenerPendientesExternos.php?matricula=${externoSeleccionado}`);
      const data = await res.json();
      const lista = data.success ? data.data : [];
      setDatosPendientes(lista);
      return lista;
    } catch (error) {
      setDatosPendientes([]);
      return [];
    } finally {
      setCargandoPendientes(false);
    }
  };

  const calcularFaltante = (referencia) => {
    if (!referencia) return 0;
    const total = parseFloat(referencia.importe_total || 0);
    const abonado = parseFloat(referencia.abonado || 0);
    return +(total - abonado).toFixed(2);
  };

  const abrirModalValidacion = (pendiente) => {
    setRefSeleccionada(pendiente);
    setFolioBanco("");
    setBancoSeleccionado(pendiente?.estatus === "PARCIAL" ? "2" : "");
    setModoModal("validar");
    setModalValidacion(true);
  };

  const abrirModalComplemento = (pendiente) => {
    setRefSeleccionada(pendiente);
    setFolioBanco("");
    setBancoSeleccionado("");
    setModoModal("complemento");
    setModalValidacion(true);
  };

  const cerrarModal = () => {
    setModalValidacion(false);
    setRefSeleccionada(null);
    setFolioBanco("");
    setBancoSeleccionado("");
    setModoModal("validar");
  };

  const refrescarTodo = async () => {
    handleAbrirHistorial();
    const lista = await handleAbrirPendientes();
    // Si ya no queda nada por validar, cerramos el modal
    if (lista.length === 0) {
      setModalPendientes(false);
    }
  };

  // --- VALIDACIÓN NORMAL Y SEGUNDO VOUCHER ---
  const confirmarValidacionPago = async (discrepancia = null) => {
    if (!folioBanco.trim()) {
      Swal.fire('Error', 'Ingresa el folio de autorización.', 'error');
      return;
    }

    setLoadingValidacion(true);
    try {
      const esComplemento = refSeleccionada?.estatus === "PARCIAL";

      let url, payload;

      if (esComplemento) {
        url = `${API_BASE}/wMultipagosCobrarComplemento.php`;
        payload = {
          id_intencion_origen: refSeleccionada.id,
          id_cajero: user?.id_usuario,
          folio_autorizacion: folioBanco,
          id_origen: 1,
          importe: calcularFaltante(refSeleccionada)
        };
      } else {
        url = `${API_BASE}/wMultipagosValidarPagoExterno.php`;
        payload = {
          id_intencion: refSeleccionada.id,
          id_cajero: user?.id_usuario,
          folio_banco: folioBanco,
          metodo_pago: bancoSeleccionado,
          discrepancia: discrepancia
        };
      }

      const respuesta = await apiFetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      const resultado = await respuesta.json();

      if (resultado.success) {
        Swal.fire({
          title: resultado.codigo === "PARCIAL" ? "Abono registrado" : "¡Pago Validado!",
          text: resultado.message,
          icon: resultado.codigo === "PARCIAL" ? "info" : "success",
          didOpen: () => {
            const cont = document.querySelector('.swal2-container');
            if (cont) cont.style.zIndex = '99999';
          }
        });

        cerrarModal();
        refrescarTodo();
      } else {
        Swal.fire('Error', resultado.message || "No se pudo validar el pago.", 'error');
      }
    } catch (error) {
      Swal.fire('Error', 'Error al procesar validación.', 'error');
    } finally {
      setLoadingValidacion(false);
    }
  };

  // --- COBRO DEL FALTANTE CON PINPAD ---
  const cobrarFaltantePinpad = async (referencia) => {
    const faltante = calcularFaltante(referencia);
    if (faltante <= 0) return;

    setLoadingValidacion(true);
    try {
      const refBase = (referencia.referencia_banorte || "").substring(0, 26);
      const referenciaTerminal = refBase + Date.now().toString().slice(-4);

      // Antes de pasar la tarjeta: si hay una version nueva publicada, no cobrar
      if (!(await asegurarVersionVigente())) return;

      const resPinpad = await fetch(PINPAD_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          Monto: faltante,
          Referencia: referenciaTerminal,
          Concepto: "COMPLEMENTO DE PAGO"
        })
      });
      const dataPinpad = await resPinpad.json();

      if (dataPinpad.status !== "Aprobado") {
        Swal.fire("Pago Declinado", `La terminal reportó: ${dataPinpad.mensaje}`, "error");
        return;
      }

      await registrarComplemento(referencia, dataPinpad.autorizacion, 2, faltante, true);
    } catch (error) {
      console.error("Error con la Pinpad:", error);
      Swal.fire("Error de Terminal", "No se pudo conectar con la Pinpad. Verifica que la aplicación local esté abierta.", "error");
    } finally {
      setLoadingValidacion(false);
    }
  };

  // --- COBRO DEL FALTANTE CON TPV ---
  const cobrarFaltanteTPV = async (referencia, folioAutorizacion) => {
    const faltante = calcularFaltante(referencia);
    if (faltante <= 0) return;

    setLoadingValidacion(true);
    try {
      await registrarComplemento(referencia, folioAutorizacion, 3, faltante, false);
    } finally {
      setLoadingValidacion(false);
    }
  };

  const registrarComplemento = async (referencia, folioAutorizacion, idOrigen, importe, avisarSiFalla) => {
    try {
      const respuesta = await apiFetch(`${API_BASE}/wMultipagosCobrarComplemento.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id_intencion_origen: referencia.id,
          id_cajero: user?.id_usuario,
          folio_autorizacion: folioAutorizacion,
          id_origen: idOrigen,
          importe: importe
        })
      });
      const resultado = await respuesta.json();

      if (resultado.success) {
        Swal.fire({
          title: "¡Pago Completado!",
          text: resultado.message,
          icon: "success",
          didOpen: () => {
            const cont = document.querySelector('.swal2-container');
            if (cont) cont.style.zIndex = '99999';
          }
        });
        cerrarModal();
        refrescarTodo();
      } else if (avisarSiFalla) {
        Swal.fire(
          "Atención: cobro retenido",
          `El pago SÍ pasó en la terminal (autorización ${folioAutorizacion}), pero no se guardó en el sistema: ${resultado.message}`,
          "warning"
        );
      } else {
        Swal.fire("No se pudo registrar", resultado.message, "error");
      }
    } catch (error) {
      console.error("Error al registrar complemento:", error);
      Swal.fire("Error crítico", "No se pudo registrar el complemento.", "error");
    }
  };

  return {
    datosHistorial, cargandoHistorial, modalPendientes, setModalPendientes,
    datosPendientes, cargandoPendientes, modalValidacion, setModalValidacion,
    refSeleccionada, folioBanco, setFolioBanco, loadingValidacion,
    bancoSeleccionado, setBancoSeleccionado,
    handleAbrirHistorial, handleAbrirPendientes, abrirModalValidacion, confirmarValidacionPago,
    abrirModalComplemento, cerrarModal, cobrarFaltantePinpad, cobrarFaltanteTPV,
    calcularFaltante, tienePinpad, modoModal
  };
};

export default useHistorialExternos;